import Component from '@glimmer/component';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Chip from 'tio-ui/components/chip';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';

interface ReportStatusBadgeSignature {
  Args: {
    report?: Report;
    hasUnsavedChanges?: boolean;
  };
  Element: HTMLDivElement;
}

export default class ReportStatusBadge extends Component<ReportStatusBadgeSignature> {
  @service declare intl: IntlService;

  get saveStateIntent(): 'success' | 'warning' | 'default' {
    if (!this.args.report) return 'default';
    if (this.args.hasUnsavedChanges) return 'warning';
    return 'success';
  }

  get saveStateLabel(): string {
    if (!this.args.report) return this.intl.t('common.reporting.status.new');
    if (this.args.hasUnsavedChanges) return this.intl.t('common.reporting.status.unsaved_changes');
    return this.intl.t('common.reporting.status.saved');
  }

  get scopeIntent(): 'primary' | 'default' {
    return this.args.report?.isPublished ? 'primary' : 'default';
  }

  get scopeLabel(): string {
    if (!this.args.report) return '';
    return this.intl.t(
      this.args.report.isPublished
        ? 'common.reporting.status.published'
        : 'common.reporting.status.unpublished'
    );
  }

  get visibilityLabel(): string {
    if (!this.args.report) return '';
    return this.args.report.visibilityLabel;
  }

  <template>
    <div class="tio-row-tight" data-test-report-status-badge ...attributes>
      <Chip @intent={{this.saveStateIntent}} @size="sm">
        {{this.saveStateLabel}}
      </Chip>
      {{#if @report}}
        <Chip @intent={{this.scopeIntent}} @size="sm">
          {{this.scopeLabel}}
        </Chip>
        <span class="text-xs tio-fg-muted">
          {{this.visibilityLabel}}
        </span>
      {{/if}}
    </div>
  </template>
}
